import { AxiosResponse } from 'axios';
import { accountCreate, accountDelete, accountList, accountRelogin, accountUpdate } from '@/api/account';
import { AppError } from '@/errors/appError';
import type { AccountItem } from '@/models/account';
import { baseListService, isRequestCanceled } from '@/services/baseService';
import { IListParams } from '@/types';
import { IAccountApiListResponse, IAccountFilters, IAccountUpdateParams, IAccountUpdateResponse } from '@/types/account';

export const accountService = {
    async list(params: IListParams<IAccountFilters>): Promise<AxiosResponse<IAccountApiListResponse>> {
        try {
            const response = await baseListService<IAccountApiListResponse>(accountList, params);
            return response;
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Account API Error:', e);
            throw new AppError(`Unable to load account list, request error`, { cause: e });
        }
    },

    async save(item: AccountItem): Promise<AxiosResponse<IAccountUpdateResponse>> {
        const row = item.toApiData();
        if (item.id) {
            return this.update({ rows: [row], requiredData: ['Stats'] });
        } else {
            return this.create({ rows: [row], requiredData: ['Stats'] });
        }
    },

    async update(params: IAccountUpdateParams): Promise<AxiosResponse<IAccountUpdateResponse>> {
        try {
            return await accountUpdate(params);
        } catch (e) {
            console.error('Account update API Error:', e);
            throw new AppError(`Unable to update account, request error`, { cause: e });
        }
    },

    async create(params: IAccountUpdateParams): Promise<AxiosResponse<IAccountUpdateResponse>> {
        try {
            return await accountCreate(params);
        } catch (e) {
            console.error('Account create API Error:', e);
            throw new AppError(`Unable to create account, request error`, { cause: e });
        }
    },

    async delete(id: number, requiredData?: string[]): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await accountDelete({ rows: [{ id }], ...(requiredData?.length ? { requiredData } : {}) });
        } catch (e) {
            console.error('Account delete API Error:', e);
            throw new AppError(`Unable to delete account, request error`, { cause: e });
        }
    },

    async deleteRows(ids: number[], requiredData?: string[]): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await accountDelete({
                rows: ids.map((id) => ({ id })),
                ...(requiredData?.length ? { requiredData } : {}),
            });
        } catch (e) {
            console.error('Account delete API Error:', e);
            throw new AppError(`Unable to delete accounts, request error`, { cause: e });
        }
    },

    async relogin(params: IAccountUpdateParams): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await accountRelogin(params);
        } catch (e) {
            console.error('Account relogin API Error:', e);
            throw new AppError(`Unable to relogin account, request error`, { cause: e });
        }
    },
};
